/**
 * Dystopia chrome — asset-driven cubicle composer.
 *
 * Spec: ../BLUEPRINT.md § The cubicle.
 */
import type { GameScene } from "../../scene/src/scenes/GameScene";
import bgUrl from "./assets/background.png";
import leftMonUrl from "./assets/left-monitor.png";
import rightMonUrl from "./assets/right-monitor.png";
import deskFgUrl from "./assets/keyboard+mug+mic.png";

export type LiveMonitor = "game-live" | "chat-live";
export type ChromeStripStage = "scene1" | "scene2" | "scene3";

export interface CubicleApi {
  showBanner(text: string, kind?: "paystub" | "compliance" | "bolide"): void;
  showShop(): void;
  hideShop(): void;
  setLiveMonitor(state: LiveMonitor): void;
  getLiveMonitor(): LiveMonitor;
  endPreLaunch(): void;
  setChromeStrip(stage: ChromeStripStage): void;
  updateDebtCounter(text: string, highlight?: boolean): void;
  onMonitorClick(handler: (which: "chat" | "game") => void): void;
}

const MOBILE_QUERY = "(max-width: 768px)";

export function initCubicle(scene: GameScene): CubicleApi | null {
  if (typeof window === "undefined") return null;

  const root = document.getElementById("cubicle");
  if (!root) throw new Error("#cubicle not found in DOM");
  const appRoot = document.getElementById("app");
  const chatPanel = document.getElementById("chat-panel");
  const gameContainer = document.getElementById("game");
  if (!appRoot || !chatPanel || !gameContainer) {
    throw new Error("cubicle init: missing #app/#chat-panel/#game");
  }

  // ─── Build layer stack ─────────────────────────────────────────
  root.innerHTML = "";

  const stageEl = document.createElement("div");
  stageEl.className = "cubicle-stage";

  const bgImg = document.createElement("img");
  bgImg.src = bgUrl;
  bgImg.alt = "";

  const leftMonImg = document.createElement("img");
  leftMonImg.src = leftMonUrl;
  leftMonImg.alt = "";

  const rightMonImg = document.createElement("img");
  rightMonImg.src = rightMonUrl;
  rightMonImg.alt = "";

  const screenLeft = document.createElement("div");
  screenLeft.className = "cubicle-screen-left";

  const screenRight = document.createElement("div");
  screenRight.className = "cubicle-screen-right";

  const deskFgImg = document.createElement("img");
  deskFgImg.src = deskFgUrl;
  deskFgImg.alt = "";

  const wallDeskLayer = document.createElement("div");
  wallDeskLayer.className = "cubicle-layer cubicle-layer-wall-desk";
  wallDeskLayer.append(bgImg);

  const monitorLeftLayer = document.createElement("div");
  monitorLeftLayer.className = "cubicle-layer cubicle-layer-monitor-left";
  monitorLeftLayer.append(leftMonImg);

  const monitorRightLayer = document.createElement("div");
  monitorRightLayer.className = "cubicle-layer cubicle-layer-monitor-right";
  monitorRightLayer.append(rightMonImg);

  const deskFgLayer = document.createElement("div");
  deskFgLayer.className = "cubicle-layer cubicle-layer-desk-fg";
  deskFgLayer.append(deskFgImg);

  stageEl.append(
    wallDeskLayer,
    monitorLeftLayer,
    monitorRightLayer,
    screenLeft,
    screenRight,
    deskFgLayer,
  );
  root.append(stageEl);

  // ─── Shop element refs (used by reparent + shop swap) ─────────
  const shopEl = document.getElementById("shop");
  const shopOriginalParent = shopEl?.parentElement ?? null;

  // ─── Mobile detection ──────────────────────────────────────────
  const mq = window.matchMedia(MOBILE_QUERY);
  let isMobile = mq.matches;

  // ─── Relocate live widgets into screen regions (desktop only) ──
  // Handedness: chat-left, viewport-right (BLUEPRINT § The cubicle — Desktop).
  if (!isMobile) {
    screenLeft.append(chatPanel);
    screenRight.append(gameContainer);
    // Phaser FIT mode caches parent dimensions; refresh after reparent.
    scene.scale.refresh();
  }

  mq.addEventListener("change", (e) => {
    isMobile = e.matches;
    if (isMobile) {
      // desktop → mobile: return shop to its original parent first so the
      // right monitor isn't holding it when chat/game move out.
      if (shopEl && shopEl.parentElement === screenRight && shopOriginalParent) {
        shopOriginalParent.append(shopEl);
      }
      appRoot.append(chatPanel);
      appRoot.append(gameContainer);
      document.body.append(strip);
      detachMonitorClicks();
      monitorLeftLayer.style.pointerEvents = "";
      monitorRightLayer.style.pointerEvents = "";
    } else {
      screenLeft.append(chatPanel);
      screenRight.append(gameContainer);
      stageEl.append(strip);
      attachMonitorClicks();
      monitorLeftLayer.style.pointerEvents = "auto";
      monitorRightLayer.style.pointerEvents = "auto";
    }
    scene.scale.refresh();
  });

  // ─── Chrome strip ──────────────────────────────────────────────
  const debtText = (n: string) => `LEASE-TO-OWN ${n} · AUTOPAY ACTIVE`;

  const STRIP_SNAPSHOTS: Record<ChromeStripStage, {
    operator: string;
    stipend: string;
    debt: string;
    biometrics: string;
  }> = {
    scene1: {
      operator:   "OPERATOR 4471",
      stipend:    "STIPEND Y1 / D003",
      debt:       "318,442",
      biometrics: "HYDRATION 64% · FINE-MOTOR NOMINAL · SLEEP DEFICIT 9H",
    },
    scene2: {
      operator:   "OPERATOR 4471",
      stipend:    "STIPEND Y1 / D004",
      debt:       "322,108",
      biometrics: "HYDRATION 58% · FINE-MOTOR DRIFT 4% · SLEEP DEFICIT 14H",
    },
    scene3: {
      operator:   "OPERATOR 4471",
      stipend:    "STIPEND Y1 / D005",
      debt:       "341,890",
      biometrics: "HYDRATION 49% · FINE-MOTOR DRIFT PERSISTENT · SLEEP DEFICIT 22H",
    },
  };

  const strip = document.createElement("div");
  strip.id = "chrome-strip";
  const s1 = STRIP_SNAPSHOTS.scene1;
  const segEls: Record<"operator" | "stipend" | "debt" | "biometrics", HTMLDivElement> = {
    operator:   document.createElement("div"),
    stipend:    document.createElement("div"),
    debt:       document.createElement("div"),
    biometrics: document.createElement("div"),
  };
  segEls.operator.className   = "cs-segment cs-operator";
  segEls.stipend.className    = "cs-segment cs-stipend";
  segEls.debt.className       = "cs-segment cs-debt";
  segEls.biometrics.className = "cs-segment cs-biometrics";
  segEls.operator.textContent   = s1.operator;
  segEls.stipend.textContent    = s1.stipend;
  segEls.debt.textContent       = debtText(s1.debt);
  segEls.biometrics.textContent = s1.biometrics;
  strip.append(segEls.operator, segEls.stipend, segEls.debt, segEls.biometrics);

  if (isMobile) {
    document.body.append(strip);
  } else {
    stageEl.append(strip);
  }

  function setChromeStrip(stage: ChromeStripStage): void {
    const snap = STRIP_SNAPSHOTS[stage];
    segEls.operator.textContent   = snap.operator;
    segEls.stipend.textContent    = snap.stipend;
    segEls.debt.textContent       = debtText(snap.debt);
    segEls.debt.classList.remove("is-highlighted");
    segEls.biometrics.textContent = snap.biometrics;
  }
  function updateDebtCounter(text: string, highlight?: boolean): void {
    segEls.debt.textContent = debtText(text);
    segEls.debt.classList.toggle("is-highlighted", !!highlight);
  }

  // ─── Banner ────────────────────────────────────────────────────
  const banner = document.createElement("div");
  banner.id = "cubicle-banner";
  document.body.append(banner);

  let bannerTimer: ReturnType<typeof setTimeout> | null = null;
  function showBanner(text: string, kind?: "paystub" | "compliance" | "bolide"): void {
    banner.className = "";
    if (kind) banner.classList.add(`kind-${kind}`);
    banner.textContent = text;
    // Force reflow so the transition fires.
    void banner.offsetWidth;
    banner.classList.add("visible");
    if (bannerTimer) clearTimeout(bannerTimer);
    bannerTimer = setTimeout(() => banner.classList.remove("visible"), 2000);
  }

  // ─── Shop swap (right-monitor takeover on desktop) ─────────────
  function showShop(): void {
    if (isMobile || !shopEl) return;
    screenRight.append(shopEl);
  }
  function hideShop(): void {
    if (isMobile || !shopEl || !shopOriginalParent) return;
    shopOriginalParent.append(shopEl);
    scene.scale.refresh();
  }

  // ─── Mobile pre-launch beat: chat full-screen until first deploy ─
  // Lifted by harness via endPreLaunch() at the Gate 3 launch tick.
  if (isMobile) {
    document.body.classList.add("pre-launch");
  }
  function endPreLaunch(): void {
    document.body.classList.remove("pre-launch");
  }

  // ─── Monitor click (desktop-only; chat/game intent) ────────────
  let monitorClickHandler: ((which: "chat" | "game") => void) | null = null;
  const onChatClick = () => monitorClickHandler?.("chat");
  const onGameClick = () => monitorClickHandler?.("game");
  function attachMonitorClicks(): void {
    monitorLeftLayer.addEventListener("click", onChatClick);
    screenLeft.addEventListener("click", onChatClick);
    monitorRightLayer.addEventListener("click", onGameClick);
    screenRight.addEventListener("click", onGameClick);
  }
  function detachMonitorClicks(): void {
    monitorLeftLayer.removeEventListener("click", onChatClick);
    screenLeft.removeEventListener("click", onChatClick);
    monitorRightLayer.removeEventListener("click", onGameClick);
    screenRight.removeEventListener("click", onGameClick);
  }
  if (!isMobile) attachMonitorClicks();
  function onMonitorClick(handler: (which: "chat" | "game") => void): void {
    monitorClickHandler = handler;
  }

  // The monitor frame layers default to `pointer-events: none`; enable them
  // on desktop so click listeners fire (screen rects already opt in).
  monitorLeftLayer.style.pointerEvents = isMobile ? "" : "auto";
  monitorRightLayer.style.pointerEvents = isMobile ? "" : "auto";

  // ─── Live monitor + chair-roll parallax ────────────────────────
  let liveMonitor: LiveMonitor = "game-live";
  function setLiveMonitor(state: LiveMonitor): void {
    if (isMobile) return;
    liveMonitor = state;
    const cs = getComputedStyle(stageEl);
    const max = cs.getPropertyValue("--max-chair-shift").trim();
    const sLive = cs.getPropertyValue("--monitor-scale-live").trim();
    const sDead = cs.getPropertyValue("--monitor-scale-inactive").trim();
    const shift = state === "game-live" ? `-${max}` : max;
    const leftScale = state === "chat-live" ? sLive : sDead;
    const rightScale = state === "game-live" ? sLive : sDead;
    root!.style.setProperty("--chair-shift", shift);
    root!.style.setProperty("--monitor-left-current-scale", leftScale);
    root!.style.setProperty("--monitor-right-current-scale", rightScale);
  }
  function getLiveMonitor(): LiveMonitor {
    return liveMonitor;
  }
  // Sync inline --chair-shift with the JS source-of-truth.
  setLiveMonitor("chat-live");

  const api: CubicleApi = {
    showBanner,
    showShop,
    hideShop,
    setLiveMonitor,
    getLiveMonitor,
    endPreLaunch,
    setChromeStrip,
    updateDebtCounter,
    onMonitorClick,
  };

  // ─── Tune mode (build-time only) ───────────────────────────────
  if (new URLSearchParams(location.search).has("tune")) {
    void import("./cubicleTune").then(({ initTunePanel }) =>
      initTunePanel(stageEl, api),
    );
  }

  return api;
}
